import { createContext, useContext, type ReactNode } from "react";
import type { AllRepositories, CopyPlan, TemplateSyncPlan } from "@pack-it-up/core";
import type { DomainError, Result } from "@pack-it-up/shared";
import type { Clock } from "@pack-it-up/viewmodel";

// The composed backend + capabilities the signed-in subtree needs to build its
// session-scoped stores. Provided once at startup (alongside the root stores) so the
// session stores can be (re)built when the user changes — without the app importing
// `data` (only composition.ts does; these are interface references).
export interface SessionDeps {
  repos: AllRepositories;
  clock: Clock;
  // Doc 13 §5 — the sync-back applier. Not part of `AllRepositories` (it applies a plan
  // as one chunked batch rather than implementing a repository interface), so the
  // composition root hands it over by name instead of the app probing for it.
  applyTemplateSyncPlan: (plan: TemplateSyncPlan) => Promise<Result<void, DomainError>>;
  // Issue #8 — the copy applier, handed over the same way and for the same reason:
  // duplicate a template, save a trip as a template, copy a trip.
  applyCopyPlan: (plan: CopyPlan) => Promise<Result<void, DomainError>>;
}

const SessionDepsContext = createContext<SessionDeps | null>(null);

export const SessionDepsProvider = ({ deps, children }: { deps: SessionDeps; children: ReactNode }): ReactNode => (
  <SessionDepsContext.Provider value={deps}>{children}</SessionDepsContext.Provider>
);

export const useSessionDeps = (): SessionDeps => {
  const deps = useContext(SessionDepsContext);
  if (deps === null) throw new Error("useSessionDeps must be used within <SessionDepsProvider>");
  return deps;
};
